/**
 * Bundle props are passed on the initial load from the server. They should be
 * configuration values that are needed globally in the application and do not
 * change during the lifetime of the application.
 *
 * Exemption: whiteLabelStyles and customLogoUri, which can be updated dynamically
 * to support live updates from the white-labeling editor.
 */
import { useEffect } from 'react';
import { atom, useAtomValue, useAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import invariant from 'invariant';
import { WhiteLabelStyles } from 'signer-app/white-labeling/types';

const bundlePropsAtom = atom<BundleProps>({} as BundleProps);

export type BundleProps = {
  whiteLabelStyles: WhiteLabelStyles;
  /**
   * The `whiteLabelStyles` will always be present in the response, so we
   * need this extra flag to know if we're using customized styles
   */
  hasCustomStyles: boolean;

  customLogoUri: string | undefined;

  signerDbxAccountId?: string;
  signerAccountId?: string;
  tsmGuid: string;
  embeddedData?: {
    typeCode: 'S';
    parentUrl: string;
    redirectUrl: string;
    isEmbeddedSigning: boolean;
    signatureId: string;
    clientId?: string;
  };
  signers?: {
    key: string;
    name: string;
    email: string;
    tsmGuid: string;
  }[];
  showReassignOnLoad?: boolean;
  sessionRecovery?: 'login' | 'reopen-link';
};

export function useHydrateBundleProps(props: BundleProps) {
  useHydrateAtoms([[bundlePropsAtom, props]]);
}

export const useUpdateWhiteLabelingBundleProps = (
  {
    whiteLabelStyles,
    customLogoUri,
  }: {
    whiteLabelStyles: WhiteLabelStyles;
    customLogoUri: string | undefined;
  },
  { enabled }: { enabled: boolean },
) => {
  const [bundleProps, setBundleProps] = useAtom(bundlePropsAtom);

  useEffect(() => {
    if (
      enabled &&
      (bundleProps.whiteLabelStyles !== whiteLabelStyles ||
        bundleProps.customLogoUri !== customLogoUri)
    ) {
      setBundleProps((prev) => ({ ...prev, whiteLabelStyles, customLogoUri }));
    }
  }, [
    enabled,
    whiteLabelStyles,
    customLogoUri,
    bundleProps.whiteLabelStyles,
    bundleProps.customLogoUri,
    setBundleProps,
  ]);
};

export function useBundleProps() {
  const bundleProps = useAtomValue(bundlePropsAtom);

  invariant(
    bundleProps,
    'Bundle props must be hydrated before they can be used',
  );

  return bundleProps;
}
